/* eslint-disable max-len */
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosProgressEvent, AxiosResponse, CancelTokenSource, isCancel } from 'axios';

import { action, runInAction, makeObservable } from 'mobx';

import DetailsActivities from 'ui/stores/DetailsActivities';

import { ActivityListResponse, ActivityResponse } from 'ui/types';
import { ActivityType } from 'utils/stores/Activities/constants';

import { PaginationParams } from 'utils/api/types';

import { TaskListResponse } from 'utils/api/modules/tasks/tasks.types';

import Logger from 'utils/logger';

import API from 'utils/api';
import RootStore from 'stores/Root';

import { EventType } from 'utils/events/constants';
import { postMessage } from 'utils/events/broadcast';
import { ActionEvent } from 'utils/events/types';

export default class AnswerActivities extends DetailsActivities<RootStore, typeof API> {
  inspectionId: string;

  answerId: string;

  constructor(rootStore: RootStore, api: typeof API, inspectionId: string, answerId: string) {
    super(rootStore, api);

    makeObservable(this);

    this.inspectionId = inspectionId;

    this.answerId = answerId;

    eventBus.register(this);
  }

  async loadActivitiesData(params: PaginationParams): Promise<AxiosResponse<ActivityListResponse>> {
    return this.api.workflows.loadActivitiesAnswer(this.inspectionId, this.answerId, params)();
  }

  @action.bound
  async addMessage(message: string): Promise<void> {
    if (this.error || !this.activities || message.trim() === '') {
      return;
    }
    try {
      const { data } = await this.api.workflows.addAnswerMessage(this.inspectionId, this.answerId, message)();
      runInAction(() => {
        if (this.activities) {
          this.activities.unshift({
            ...data,
            type: ActivityType.Message,
          });
          postMessage(EventType.AddedMessage, {
            ...data,
            inspectionId: this.inspectionId,
            questionId: this.answerId,
          });
        }
      });
    } catch (e) {
      Logger.error(
        `Invalid add message API result: Inspection ID: ${this.inspectionId}. Answer ID: ${this.answerId}. Message: ${message}`,
        e
      );
    }
  }

  @action.bound
  async deleteMessage(messageId: string): Promise<void> {
    if (this.activities === undefined) {
      return;
    }
    const message = this.activities.find((activity) => activity.uuid === messageId);
    if (message) {
      try {
        await this.api.workflows.removeAnswerMessage(this.inspectionId, this.answerId, messageId)();
        runInAction(() => {
          if (this.activities !== undefined) {
            this.activities = this.activities.filter((activity) => activity.uuid !== messageId);
          }
          postMessage(EventType.DeletedMessage, {
            inspectionId: this.inspectionId,
            questionId: this.answerId,
          });
        });
      } catch (e) {
        Logger.error(
          `Invalid delete message API result: Inspection ID: ${this.inspectionId}. Answer ID: ${this.answerId}. Message ID: ${messageId}`,
          e
        );
      }
    }
  }

  async doUploadFile(
    file: File,
    onUpdateProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): Promise<void> {
    try {
      const { data } = await this.api.workflows.uploadInspectionFile(
        this.inspectionId,
        this.answerId,
        file,
        onUpdateProgress,
        cancelTokenSource
      )();
      runInAction(() => {
        if (this.activities) {
          this.activities.unshift(data as unknown as ActivityResponse);
          postMessage(EventType.AddedFile, {
            ...data,
            inspectionId: this.inspectionId,
            questionId: this.answerId,
          });
        }
      });
    } catch (e) {
      if (isCancel(e)) {
        return;
      }
      Logger.error(
        `Invalid add file API result: Inspection ID: ${this.inspectionId}. Question ID: ${this.answerId}.`,
        e
      );
      this.rootStore.notification.enqueueErrorSnackbar('Failed to upload file');
    }
  }

  @action.bound
  async deleteFile(fileId: string): Promise<void> {
    if (this.activities === undefined) {
      return;
    }

    try {
      await this.api.workflows.removeInspectionFile(this.inspectionId, this.answerId, fileId)();
      runInAction(() => {
        this.removeActivity(fileId);
        postMessage(EventType.DeletedFile, {
          inspectionId: this.inspectionId,
          questionId: this.answerId,
        });
      });
    } catch (e) {
      Logger.error(
        `Invalid delete file API result: Inspection ID: ${this.inspectionId}. Question ID: ${this.answerId}. File ID: ${fileId}`,
        e
      );
    }
  }

  @subscribe(EventType.CreatedTask)
  @subscribe(EventType.CreatedTaskAnswerTask)
  @action
  createdTask({ payload }: ActionEvent<TaskListResponse>): void {
    if (this.activities !== undefined && payload.links.some((link) => link.instance.uuid === this.inspectionId)) {
      this.activities = undefined;
      this.loadActivities();
    }
  }

  @subscribe(EventType.DeletedFile)
  @action
  onFileDeleted({ payload }: ActionEvent<{ inspectionId?: string; fileId?: string }>): void {
    if (this.activities === undefined || payload.inspectionId !== this.inspectionId || !payload.fileId) {
      return;
    }

    if (this.activities.some((a) => a.uuid === payload.fileId)) {
      this.activities = undefined;
      this.loadActivities();
    }
  }
}
